import React, { forwardRef, type ComponentPropsWithRef } from 'react';

import styled from '@emotion/styled';
import { variant } from '@lucid/theme';

import decorate from '../decorate/decorate';
import type { Lucid } from '../../types/types';
import type { LinkOptions } from '../link/link';
import Text from '../text/text';

export type HTMLButtonProps = Omit<ComponentPropsWithRef<'button'>, 'size' | 'type'>;

export interface TabProps extends LinkOptions, HTMLButtonProps {
  /** The children passed to the Button component. */
  children?: React.ReactNode;
  /** Allows the component to get additional styles. */
  className?: string;
  /** The available variants for the Tab component. */
  variant?: Lucid.Theme.Component.Tab.VariantKeys;
  /** Checks if the button should be disabled. */
  isDisabled?: boolean;
  /** Determines if the tab is the active tab */
  isActive?: boolean;
}

const StyledTab = styled.button<TabProps>(
  variant({
    scale: 'tab',
  }),
  {
    label: 'tabButton',
  },
  ({ isActive, variant, theme }) =>
    isActive && {
      margin: 0,
      '&:hover > p': {
        color: theme.colors.coolGrey[100],
      },
      border: `1px solid ${
        variant?.includes('Grey')
          ? theme.colors.coolGrey[100]
          : theme.colors.productColor({
              co: theme.colors.blue[70],
              chart: theme.colors.orange[70],
              scale: theme.colors.indigo[70],
              spark: theme.colors.red[70],
            })
      }`,
      backgroundColor: variant?.includes('Grey')
        ? theme.colors.coolGrey[100]
        : theme.colors.productColor({
            co: theme.colors.blue[70],
            chart: theme.colors.orange[70],
            scale: theme.colors.indigo[70],
            spark: theme.colors.red[70],
          }),
    },
);

const TabComponent = forwardRef<HTMLButtonElement, TabProps>(
  ({ className, href, isDisabled = false, label, isActive = false, variant = 'primary', ...rest }, forwardedRef) => (
    <StyledTab
      {...rest}
      ref={forwardedRef}
      aria-disabled={isDisabled || rest['aria-disabled']}
      className={className}
      data-testid="tab-button"
      disabled={isDisabled}
      href={href}
      isActive={isActive}
      type="button"
      variant={variant}
    >
      <Text
        css={theme => [
          { margin: 0 },
          isActive && {
            color: theme.colors.white,
          },
        ]}
        variant={isActive ? 'interactiveM500' : 'interactiveM400'}
      >
        {label}
      </Text>
    </StyledTab>
  ),
);

// manually adding display name or it goes missing on build
TabComponent.displayName = 'Tab';
const Tab = decorate(TabComponent);

export { Tab };
export default Tab;
